import { AssetTypeCode, DEFAULT_PM_RULE_SOURCE, WORKFRONT_LOOKUP_LABELS, WorkfrontLookup } from '@atlas/types'
import { FieldShell, SelectField } from '@atlas/ui-components'
import { Input } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import type { AssetTypeRow, LookupRow, ValueRow } from '@/services/workfront-mappings.service'
import { humanize } from '../../components/StatusBadges'
import { type FormProps, valueOptions, valuePath } from './drawer-context'
import { ErrorLine } from './FieldMappingForm'

/** The Workfront labels a value form maps: one, or several to one value. */
function LabelList({ labels }: { labels: string[] }) {
	return (
		<div>
			<p className="font-medium text-ds-sm">{labels.length === 1 ? 'Workfront label' : 'Workfront labels'}</p>
			<ul className="text-ds-sm">
				{labels.map((label) => (
					<li key={label}>{label}</li>
				))}
			</ul>
		</div>
	)
}

/**
 * A value mapping: the ATLAS value the label resolves to (searched on its axis,
 * shown with its path), the geography it holds in, and a way out to create the
 * value when the axis lacks it.
 */
export function ValueMappingForm({
	row,
	onChange,
	errors,
	context,
	labels,
}: FormProps<ValueRow> & { labels: string[] }) {
	const path = valuePath(context.axisValues, row.valueId)
	return (
		<div className="space-y-4">
			<LabelList labels={labels} />
			<p className="text-ds-sm text-ds-text-muted">Axis: {humanize(row.axis)}</p>
			<div>
				<SelectField
					label="ATLAS value"
					placeholder="choose"
					value={row.valueId ?? ''}
					options={valueOptions(context.axisValues)}
					isSearchable
					onChange={(valueId) => onChange({ valueId })}
				/>
				{path ? <p className="mt-1 text-ds-xs text-ds-text-muted">{path}</p> : null}
				<ErrorLine message={errors.valueId ?? errors.label ?? errors.axis} />
				{context.createValueHref ? (
					<Link className="mt-2 block w-fit text-ds-sm underline" to={context.createValueHref}>
						Create value…
					</Link>
				) : null}
			</div>
			<div>
				<SelectField
					label="Geography scope"
					value={row.geoValueId ?? ''}
					placeholder="everywhere"
					options={valueOptions(context.geographyValues)}
					isSearchable
					onChange={(geoValueId) => onChange({ geoValueId: geoValueId || null })}
				/>
				<ErrorLine message={errors.geoValueId} />
			</div>
		</div>
	)
}

/** An asset-type row: the (Classification, Asset Detail) pair and the asset type it becomes. */
export function AssetTypeMappingForm({ row, onChange, errors, context }: FormProps<AssetTypeRow>) {
	const codes = Object.values(AssetTypeCode).map((code) => ({
		value: code,
		label: context.assetTypeNames[code] ? `${code} · ${context.assetTypeNames[code]}` : code,
	}))
	return (
		<div className="space-y-4">
			<FieldShell label="Classification" htmlFor="asset-classification" error={errors.classificationValue}>
				<Input
					id="asset-classification"
					value={row.classificationValue}
					aria-invalid={!!errors.classificationValue}
					onChange={(event) => onChange({ classificationValue: event.target.value })}
				/>
			</FieldShell>
			<FieldShell label="Asset Detail" htmlFor="asset-detail" error={errors.assetDetailValue}>
				<Input
					id="asset-detail"
					value={row.assetDetailValue ?? ''}
					placeholder="none, for a classification without one"
					onChange={(event) => onChange({ assetDetailValue: event.target.value || null })}
				/>
			</FieldShell>
			<div>
				<SelectField
					label="Asset type"
					placeholder="choose"
					value={row.assetTypeCode}
					options={codes}
					isSearchable
					onChange={(assetTypeCode) => onChange({ assetTypeCode })}
				/>
				<ErrorLine message={errors.assetTypeCode} />
			</div>
		</div>
	)
}

/** A PM rule: a geography (or the default for any other) and the active PM it goes to. */
export function PmRuleForm({ row, onChange, errors, context }: FormProps<LookupRow>) {
	const geography = [
		{ value: DEFAULT_PM_RULE_SOURCE, label: 'Default (any other geography)' },
		...context.geographyValues.map((value) => ({ value: value.code, label: value.label, depth: value.depth })),
	]
	const valueId = context.geographyValues.find((value) => value.code === row.sourceValue)?.valueId
	const path = valuePath(context.geographyValues, valueId)
	return (
		<div className="space-y-4">
			<div>
				<SelectField
					label="Geography"
					placeholder="choose"
					value={row.sourceValue}
					options={geography}
					isSearchable
					onChange={(sourceValue) => onChange({ sourceValue })}
				/>
				{path ? <p className="mt-1 text-ds-xs text-ds-text-muted">{path}</p> : null}
				<ErrorLine message={errors.sourceValue} />
			</div>
			<div>
				<SelectField
					label="PM"
					placeholder="choose"
					value={row.targetValue}
					options={context.pms}
					isSearchable
					onChange={(targetValue) => onChange({ targetValue })}
				/>
				<ErrorLine message={errors.targetValue} />
			</div>
		</div>
	)
}

/** A lookup row: the Workfront value and the ATLAS value it becomes. */
export function LookupRowForm({
	row,
	onChange,
	errors,
	sourceLabel,
	context,
}: FormProps<LookupRow> & { sourceLabel?: string | null }) {
	if (row.lookup === WorkfrontLookup.REGION_TO_PM) return <PmRuleForm {...{ row, onChange, errors, context }} />
	return (
		<div className="space-y-4">
			<p className="text-ds-sm text-ds-text-muted">Lookup: {WORKFRONT_LOOKUP_LABELS[row.lookup]}</p>
			<FieldShell
				label="Workfront value"
				htmlFor="lookup-source"
				error={errors.sourceValue}
				description={sourceLabel && sourceLabel !== row.sourceValue ? sourceLabel : undefined}
			>
				<Input
					id="lookup-source"
					value={row.sourceValue}
					onChange={(event) => onChange({ sourceValue: event.target.value })}
				/>
			</FieldShell>
			<FieldShell label="ATLAS value" htmlFor="lookup-target" error={errors.targetValue}>
				<Input
					id="lookup-target"
					value={row.targetValue}
					aria-invalid={!!errors.targetValue}
					onChange={(event) => onChange({ targetValue: event.target.value })}
				/>
			</FieldShell>
		</div>
	)
}
